<template>
  <div :id="'cem-card-item-inner-' + props.item.id" style="width: 100%">
    <div
      v-if="dataTable.data.length > 0"
      class="score"
      :class="{ clickable: hasScoreAction }"
      @click="actionCardItem"
    >
      <div class="score__main">
        <div class="score__value" :class="classValue" :style="styleVarsValue">
          <EllipsisTooltip :text="scoreValue" v-if="!score.show_star" />
          <div v-else>{{ scoreValue }}</div>
        </div>

        <div class="score__stars" v-if="score.show_star">
          <StarRating :rating="rateValue" :key="keyStarRating" />
        </div>
      </div>

      <div
        class="score__subTitle"
        :class="classSubTitle"
        :style="styleVarsSubTitle"
      >
        <EllipsisTooltip :text="subTitleValue" v-if="!score.show_star" />
        <div v-else>{{ subTitleValue }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, reactive, watch, ref, computed } from "vue";
import { Card_item } from "cem-probe-api";
import StarRating from "../StarRating.vue";
import MethodService from "../../service/MethodService";
import EllipsisTooltip from "../EllipsisTooltip.vue";
import { useUrlPopupStore } from "@/stores/urlPopupStore";
const storeUrlPopup = useUrlPopupStore();
const emit = defineEmits(["showDialogDashboardPopup"]);
const props = defineProps({
  item: Card_item,
  itemData: Object,
});
const dataTable = reactive({ data: [], columns: [] });
const keyStarRating = ref(0);
const score = computed(() => props.item.score);
const subTitleValue = computed(() => props.item.score?.sub_title?.text);
const hasScoreAction = computed(() => {
  const action = props.item?.score?.action;
  return !!(action?.link || action?.popup_id);
});
const classValue = computed(() => {
  return {
    "score__value--ellipsis": !score.value?.value?.show_star,
  };
});

const classSubTitle = computed(() => {
  return {
    "score__subTitle--ellipsis": !score.value?.show_star,
  };
});

const styleVarsValue = computed(() => ({
  "--custom-font-size": `${score.value?.value?.fontSize ?? 14}px`,
  "--custom-color":
    props.itemData?.data?.[0]?.[1] || score.value?.value?.color || "#000",
}));

const styleVarsSubTitle = computed(() => ({
  "--custom-font-size": `${score.value?.sub_title?.fontSize ?? 14}px`,
  "--custom-color": score.value?.sub_title?.color || "#000",
}));

const scoreValue = computed(() => {
  const value = props.itemData?.data?.[0]?.[0];

  if (!score.value?.show_star) {
    return value == null ? "-" : value;
  }

  const isValidScore =
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 5;

  return isValidScore ? Number(value.toFixed(2)).toString() : "-";
});

const rateValue = computed(() => {
  const value = props.itemData?.data?.[0]?.[0];
  return typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 5
    ? value
    : 0;
});

const actionCardItem = () => {
  const action = props.item?.score?.action;
  if (!action || Object.keys(action).length === 0) return;

  const score = scoreValue.value;

  const hasLink = !!action.link;
  const hasPopup = !!action.popup_id;

  if (!hasLink && !hasPopup) return;

  if (hasLink) {
    const finalLink = MethodService.renderLinkChart(
      {
        value: score,
      },
      action.link
    );
    window.open(finalLink);
  } else if (hasPopup) {
    const chartUrl = MethodService.renderLinkChart(
      {
        value: score,
      },
      null
    );
    storeUrlPopup.initUrlPopup();
    storeUrlPopup.pushUrlPopup(chartUrl);
    emit("showDialogDashboardPopup", action.popup_id, chartUrl);
  }
};

watch(
  () => props.itemData,
  (newData) => {
    dataTable.columns = newData?.columns ?? [];
    dataTable.data =
      Array.isArray(newData?.data) && newData.data.length > 0
        ? newData.data
        : [];
  },
  { deep: true, immediate: true }
);
</script>

<style lang="scss">
.score {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  height: 100%;
  padding: 16px;

  &__main {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-width: 100%;
  }

  &__value {
    font-weight: bold;
    line-height: 1;
    font-size: var(--custom-font-size, 14px);
    color: var(--custom-color, #000);
    width: 100%;

    &--ellipsis {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__stars {
    margin-top: 16px;
  }

  &__subTitle {
    margin-top: auto;
    padding-top: 16px;
    font-size: var(--custom-font-size, 14px);
    color: var(--custom-color, #000);
    width: 100%;

    &--ellipsis {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
